import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ListFooter } from '../ui';

const NET_MINS = 510;

export function BulletinContent({ embedded = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [view, setView] = useState('list'); // list | create | detail
  const [bulletins, setBulletins] = useState([]);
  const [current, setCurrent] = useState(null);
  const [orders, setOrders] = useState([]);
  const [operations, setOperations] = useState([]);
  const [machines, setMachines] = useState([]);
  const [opSearch, setOpSearch] = useState('');
  const [opDivision, setOpDivision] = useState('');
  const [showAddOp, setShowAddOp] = useState(false);
  const [showNewOpMaster, setShowNewOpMaster] = useState(false);
  const [opForm, setOpForm] = useState({});
  const [newOpForm, setNewOpForm] = useState({});
  const [createForm, setCreateForm] = useState({ division:'MENS' });
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [rowForm, setRowForm] = React.useState({});
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    fetchBulletins();
    api.get('/orders').then(r=>setOrders(r.data.data||[])).catch(()=>{});
    api.get('/bulletins/machines').then(r=>setMachines(r.data.data||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    if(view==='detail' && current) {
      fetchOperationMaster();
    }
  }, [view, opSearch, opDivision]);

  const fetchBulletins = async () => {
    try {
      const r = await api.get('/bulletins');
      setBulletins(r.data.data||[]);
    } catch(e) {}
  };

  const fetchBulletinDetail = async (pb_id) => {
    try {
      const r = await api.get('/bulletins/'+pb_id);
      setCurrent(r.data.data);
    } catch(e) {}
  };

  const fetchOperationMaster = async () => {
    try {
      let url = '/bulletins/operations?';
      if(opDivision) url += 'division='+opDivision+'&';
      if(opSearch) url += 'search='+encodeURIComponent(opSearch);
      const r = await api.get(url);
      setOperations(r.data.data||[]);
    } catch(e) {}
  };

  const createBulletin = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const r = await api.post('/bulletins', createForm);
      await fetchBulletins();
      setCurrent(r.data.data);
      setView('detail');
      fetchOperationMaster();
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const addOperationWithFreq = async (op, freq) => {
    if(!current) return;
    setSaving(true);
    try {
      await api.post('/bulletins/'+current.id+'/operations', {
        process_code: op.process_code,
        process_name: op.process_name,
        machine_code: op.machine_code,
        machine_name: machines.find(m=>m.code===op.machine_code)?.name || op.machine_code,
        sam: op.standard_sam,
        cost_per_piece: op.cost_per_piece,
        frequency: freq||1,
      });
      await fetchBulletinDetail(current.id);
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const addOperation = async (op) => {
    if(!current) return;
    setSaving(true);
    try {
      await api.post('/bulletins/'+current.id+'/operations', {
        process_code: op.process_code,
        process_name: op.process_name,
        machine_code: op.machine_code,
        machine_name: machines.find(m=>m.code===op.machine_code)?.name || op.machine_code,
        sam: op.standard_sam,
        ...opForm
      });
      await fetchBulletinDetail(current.id);
      setOpForm({});
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const addCustomOperation = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const machName = machines.find(m=>m.code===opForm.machine_code)?.name || opForm.machine_code;
      await api.post('/bulletins/'+current.id+'/operations', { ...opForm, machine_name: machName });
      await fetchBulletinDetail(current.id);
      setShowAddOp(false); setOpForm({});
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const updateOperation = async (op_id) => {
    setSaving(true);
    try {
      await api.patch('/bulletins/'+current.id+'/operations/'+op_id, rowForm);
      await fetchBulletinDetail(current.id);
      setEditingRow(null); setRowForm({});
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const deleteOperation = async (op_id) => {
    if(!window.confirm('Remove this operation?')) return;
    try {
      await api.delete('/bulletins/'+current.id+'/operations/'+op_id);
      await fetchBulletinDetail(current.id);
    } catch(err) { alert('Error: '+err.message); }
  };

  const updateStatus = async (status, extra={}) => {
    try {
      await api.patch('/bulletins/'+current.id, { status, ...extra });
      await fetchBulletinDetail(current.id);
    } catch(err) { alert('Error: '+err.message); }
  };

  const saveNewOpMaster = async (e) => {
    e.preventDefault();
    const v = parseFloat(newOpForm.standard_sam);
    if (v > 0 && v < 0.1 && !window.confirm(`Standard SAM ${v} min is under 0.1 min (6 seconds) — nothing on a garment is that fast. Check it isn't in the wrong unit (seconds/minutes). Save anyway?`)) return;
    setSaving(true);
    try {
      await api.post('/bulletins/operations/new', newOpForm);
      setShowNewOpMaster(false); setNewOpForm({});
      fetchOperationMaster();
      alert('New operation added to master list!');
    } catch(err) { alert('Error: '+err.message); }
    finally { setSaving(false); }
  };

  const selectOrder = (order_id) => {
    const o = orders.find(x=>x.id===order_id);
    if(o) {
      setCreateForm(p=>({...p,
        order_id: o.id,
        gpo_no: o.gpo_no||o.order_no,
        buyer: o.buyer_name||'',
        style: o.style||o.garment_type||'',
        colour: o.colour||'',
        order_qty: o.quantity||''
      }));
    }
  };

  const inputStyle = {width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'};
  const labelStyle = {fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'};

  const criticalOp = current?.operations?.length > 0
    ? current.operations.reduce((max,op) => (parseFloat(op.sam)||0) > (parseFloat(max.sam)||0) ? op : max, current.operations[0])
    : null;

  const totalMins = current?.operations?.reduce((s,op) => s + (parseFloat(op.minutes_consumed)||0), 0) || 0;
  // Use observed (minutes_consumed) if available, else standard SAM
  const totalSAM = current?.operations?.reduce((s,op) => {
    const observed = parseFloat(op.minutes_consumed);
    const standard = parseFloat(op.sam);
    return s + (observed > 0 ? observed : (standard > 0 ? standard : 0));
  }, 0) || 0;
  const hasObservedSAM = current?.operations?.some(op => parseFloat(op.minutes_consumed) > 0);
  const totalCM = current?.operations?.reduce((s,op) => s + ((parseFloat(op.cost_per_piece)||0) * (parseInt(op.frequency)||1)), 0) || 0;

  const machinesReq = current?.machines_required;
  const dailyTarget = current?.daily_target;
  const tatDays = current?.tat_days;
  const outputPerMachine = totalSAM > 0 ? Math.round(NET_MINS / totalSAM) : 0;

  const Nav = () => null;

  if(view==='list') return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:'inherit'}}>
      <Nav/>
      <div style={{padding:24}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div>
            <h2 style={{margin:0,fontSize:20,color:'#3B3A8F'}}>Production Bulletins</h2>
            <p style={{margin:'4px 0 0',fontSize:13,color:'#888'}}>Pre-production time & motion study with auto machine planning</p>
          </div>
          <button onClick={() => setView('create')} style={{padding:'9px 20px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>+ New Bulletin</button>
        </div>
        <div style={{background:'white',borderRadius:10,boxShadow:'none',border:'1px solid #E8E7E7'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#F7F6F3'}}>
              {['PB No','GPO','Buyer','Style','Division','Order Qty','Total SAM','Machines Req','Daily Target','TAT Days','Status','Action'].map(h=>(
                <th key={h} style={{padding:'10px 12px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {bulletins.length===0 ? <tr><td colSpan={12} style={{padding:40,textAlign:'center',color:'#aaa'}}>No production bulletins yet</td></tr>
              : bulletins.map((pb,i) => (
                <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                  <td style={{padding:'10px 12px',fontFamily:'monospace',fontWeight:600,color:'#3B3A8F'}}>{pb.pb_no}</td>
                  <td style={{padding:'10px 12px',fontWeight:600}}>{pb.gpo_no}</td>
                  <td style={{padding:'10px 12px'}}>{pb.buyer}</td>
                  <td style={{padding:'10px 12px'}}>{pb.style}</td>
                  <td style={{padding:'10px 12px'}}>{pb.division}</td>
                  <td style={{padding:'10px 12px',textAlign:'right'}}>{Number(pb.order_qty||0).toLocaleString('en-IN')}</td>
                  <td style={{padding:'10px 12px',textAlign:'center',fontWeight:700,color:'#3B3A8F'}}>{pb.total_sam?Number(pb.total_sam).toFixed(2):'-'}</td>
                  <td style={{padding:'10px 12px',textAlign:'center',fontWeight:700,color:'#E22D2D'}}>{pb.machines_required||'-'}</td>
                  <td style={{padding:'10px 12px',textAlign:'center'}}>{pb.daily_target||'-'}</td>
                  <td style={{padding:'10px 12px',textAlign:'center'}}>{pb.tat_days||'-'}</td>
                  <td style={{padding:'10px 12px'}}>
                    <span style={{padding:'3px 10px',borderRadius:10,fontSize:11,fontWeight:600,
                      background:pb.status==='approved'?'#dcfce7':pb.status==='checked'?'#EEEDF8':'#fef9c3',
                      color:pb.status==='approved'?'#166534':pb.status==='checked'?'#3B3A8F':'#9A6B00'}}>
                      {pb.status}
                    </span>
                  </td>
                  <td style={{padding:'10px 12px'}}>
                    <button onClick={async()=>{ await fetchBulletinDetail(pb.id); fetchOperationMaster(); setView('detail'); }}
                      style={{padding:'4px 12px',background:'#3B3A8F',color:'white',border:'none',borderRadius:4,fontSize:12,cursor:'pointer'}}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListFooter shown={bulletins.length} total={bulletins.length} noun="bulletins" />
        </div>
      </div>
    </div>
  );

  if(view==='create') return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:'inherit'}}>
      <Nav/>
      <div style={{padding:24,maxWidth:700,margin:'0 auto'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <button onClick={() => setView('list')} style={{padding:'6px 12px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,cursor:'pointer',fontSize:13}}>← Back</button>
          <h2 style={{margin:0,fontSize:18,color:'#3B3A8F'}}>New Production Bulletin</h2>
        </div>
        <div style={{background:'white',borderRadius:10,padding:28,boxShadow:'none',border:'1px solid #E8E7E7'}}>
          <form onSubmit={createBulletin}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div style={{gridColumn:'1/-1'}}>
                <label style={labelStyle}>Select Order (GPO) *</label>
                <select style={inputStyle} required onChange={e=>selectOrder(e.target.value)}>
                  <option value="">Select order</option>
                  {orders.map(o=><option key={o.id} value={o.id}>{o.gpo_no||o.order_no} — {o.buyer_name} — {o.style||o.garment_type}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Buyer</label>
                <input style={inputStyle} value={createForm.buyer||''} onChange={e=>setCreateForm(p=>({...p,buyer:e.target.value}))} placeholder="Auto-filled from order"/>
              </div>
              <div>
                <label style={labelStyle}>Style</label>
                <input style={inputStyle} value={createForm.style||''} onChange={e=>setCreateForm(p=>({...p,style:e.target.value}))} placeholder="Auto-filled from order"/>
              </div>
              <div>
                <label style={labelStyle}>Colour</label>
                <input style={inputStyle} value={createForm.colour||''} onChange={e=>setCreateForm(p=>({...p,colour:e.target.value}))}/>
              </div>
              <div>
                <label style={labelStyle}>Division *</label>
                <select style={inputStyle} value={createForm.division} onChange={e=>setCreateForm(p=>({...p,division:e.target.value}))}>
                  <option value="MENS">Mens</option>
                  <option value="LADIES">Ladies</option>
                  <option value="KIDS">Kids</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Order Qty</label>
                <input style={inputStyle} type="number" value={createForm.order_qty||''} onChange={e=>setCreateForm(p=>({...p,order_qty:e.target.value}))}/>
              </div>
              <div>
                <label style={labelStyle}>Size Set Date</label>
                <input style={inputStyle} type="date" onChange={e=>setCreateForm(p=>({...p,size_set_date:e.target.value}))}/>
              </div>
              <div>
                <label style={labelStyle}>Prepared By</label>
                <input style={inputStyle} value={createForm.prepared_by||user?.name||''} onChange={e=>setCreateForm(p=>({...p,prepared_by:e.target.value}))}/>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button type="button" onClick={() => setView('list')} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
              <button type="submit" disabled={saving} style={{padding:'8px 20px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Creating...':'Create Bulletin'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if(view==='detail' && current) return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:'inherit'}}>
      <style>{`@media print { .no-print{display:none!important} }`}</style>
      <Nav/>
      <div style={{padding:24}}>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}} className="no-print">
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <button onClick={() => setView('list')} style={{padding:'6px 12px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,cursor:'pointer',fontSize:13}}>← Back</button>
            <div>
              <h2 style={{margin:0,fontSize:18,color:'#3B3A8F'}}>{current.pb_no}</h2>
              <p style={{margin:'2px 0 0',fontSize:12,color:'#888'}}>{current.gpo_no} · {current.buyer} · {current.style}</p>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={() => window.print()} style={{padding:'7px 16px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer'}}>🖨️ Print</button>
            {current.status==='draft' && <button onClick={() => updateStatus('checked',{checked_by:user?.name})} style={{padding:'7px 16px',background:'#f59e0b',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer'}}>✓ Mark Checked</button>}
            {current.status==='checked' && <button onClick={() => updateStatus('approved',{approved_by:user?.name})} style={{padding:'7px 16px',background:'#22c55e',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer'}}>✓ Approve</button>}
          </div>
        </div>

        {/* Auto Machine Calculator */}
        {totalSAM > 0 && current.order_qty && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
            {[
              {label:'Total SAM',value:totalSAM.toFixed(2)+' mins',color:'#3B3A8F'},
              {label:'Output/Machine/Day',value:outputPerMachine+' pcs',color:'#2A2970'},
              {label:'TAT Days',value:(tatDays||'-')+' days',color:'#555'},
              {label:'Machines Required',value:(machinesReq||Math.ceil((current.order_qty/((tatDays||1)*outputPerMachine)))||'-'),color:'#E22D2D',big:true},
              {label:'Daily Target',value:(dailyTarget||Math.round(current.order_qty/(tatDays||1)))+' pcs',color:'#3B3A8F'},
            ].map(card=>(
              <div key={card.label} style={{background:'white',borderRadius:8,padding:'14px 16px',boxShadow:'none',border:'1px solid #E8E7E7',borderTop:card.big?'3px solid #E22D2D':'3px solid #3B3A8F'}}>
                <div style={{fontSize:10,color:'#888',fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>{card.label}</div>
                <div style={{fontSize:card.big?28:22,fontWeight:900,color:card.color,marginTop:4}}>{card.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Critical Process Alert */}
        {criticalOp && (
          <div style={{background:'#fff5f5',border:'1px solid #fca5a5',borderRadius:8,padding:'12px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:20}}>⚠️</span>
            <div>
              <div style={{fontWeight:700,fontSize:13,color:'#E22D2D'}}>CRITICAL PROCESS — Maximum Time</div>
              <div style={{fontSize:13,marginTop:2}}>
                <b>{criticalOp.process_name}</b> · Operator: <b>{criticalOp.operator_name||'—'}</b> · SAM: <b>{criticalOp.sam} mins</b> · Machine: <b>{criticalOp.machine_name}</b>
              </div>
              <div style={{fontSize:11,color:'#888',marginTop:2}}>This is the bottleneck operation — focus on line balancing here</div>
            </div>
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20}}>
          {/* Operations Table */}
          <div style={{background:'white',borderRadius:10,boxShadow:'none',border:'1px solid #E8E7E7'}}>
            <div style={{padding:'14px 20px',borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontWeight:700,fontSize:14}}>Operations ({current.operations?.length||0})</div>
              <div style={{display:'flex',gap:8}} className="no-print">
                <button onClick={() => setShowAddOp(true)} style={{padding:'5px 14px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}}>+ Custom Operation</button>
                <button onClick={() => setShowNewOpMaster(true)} style={{padding:'5px 14px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:12,cursor:'pointer'}}>+ Add to Master</button>
              </div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#F7F6F3'}}>
                  {['S.No','Operator','Operation','Machine','SAM (mins)','Cost/Op (₹)','Freq','Cost/Garment (₹)','Start','End','Time Consumed','Remarks',''].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11,whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(!current.operations||current.operations.length===0) ? (
                    <tr><td colSpan={10} style={{padding:30,textAlign:'center',color:'#aaa'}}>No operations added yet — select from the master list on the right</td></tr>
                  ) : current.operations.map((op,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f5f5f5',background:criticalOp?.id===op.id?'#fff5f5':'white'}}>
                      <td style={{padding:'8px 10px',fontWeight:700,color:'#3B3A8F'}}>{op.sno}</td>
                      <td style={{padding:'6px 8px'}} onClick={()=>{setEditingRow(op.id);setRowForm({operator_name:op.operator_name||'',start_time:op.start_time||'',end_time:op.end_time||'',remarks:op.remarks||''});}}><span style={{cursor:'pointer',color:op.operator_name?'#333':'#bbb',borderBottom:'1px dashed #ddd'}}>{op.operator_name||'+ Add'}</span></td>
                      <td style={{padding:'8px 10px',fontWeight:criticalOp?.id===op.id?700:400,color:criticalOp?.id===op.id?'#E22D2D':'#333'}}>{op.process_name}{criticalOp?.id===op.id&&' ⚠️'}</td>
                      <td style={{padding:'8px 10px'}}>{op.machine_name||op.machine_code||'—'}</td>
                      <td style={{padding:'8px 10px',fontWeight:700,color:criticalOp?.id===op.id?'#E22D2D':'#3B3A8F'}}>
  {op.minutes_consumed > 0 
    ? <span>{op.minutes_consumed}<span style={{fontSize:9,color:'#22c55e',marginLeft:3}}>OBS</span></span>
    : op.sam 
      ? <span>{op.sam}<span style={{fontSize:9,color:'#f59e0b',marginLeft:3}}>STD</span></span>
      : '—'}
</td>
                      <td style={{padding:'6px 8px',color:'#166534',fontWeight:600}}>{op.cost_per_piece?'₹'+Number(op.cost_per_piece).toFixed(2):'—'}</td>
                      <td style={{padding:'6px 8px',textAlign:'center'}}>{op.frequency||1}</td>
                      <td style={{padding:'6px 8px',color:'#166534',fontWeight:700}}>{op.cost_per_garment?'₹'+Number(op.cost_per_garment).toFixed(2):op.cost_per_piece?'₹'+(Number(op.cost_per_piece)*(op.frequency||1)).toFixed(2):'—'}</td>
                      <td style={{padding:'6px 8px'}} onClick={()=>{setEditingRow(op.id);setRowForm({operator_name:op.operator_name||'',start_time:op.start_time||'',end_time:op.end_time||'',remarks:op.remarks||''});}}><span style={{cursor:'pointer',color:op.start_time?'#333':'#bbb',borderBottom:'1px dashed #ddd'}}>{op.start_time||'+ Set'}</span></td>
                      <td style={{padding:'6px 8px'}} onClick={()=>{setEditingRow(op.id);setRowForm({operator_name:op.operator_name||'',start_time:op.start_time||'',end_time:op.end_time||'',remarks:op.remarks||''});}}><span style={{cursor:'pointer',color:op.end_time?'#333':'#bbb',borderBottom:'1px dashed #ddd'}}>{op.end_time||'+ Set'}</span></td>
                      <td style={{padding:'6px 8px'}}>{op.minutes_consumed?op.minutes_consumed+' mins':'—'}</td>
                      <td style={{padding:'6px 8px',color:'#888'}}>{op.remarks||'—'}</td>
                      <td style={{padding:'8px 10px'}} className="no-print">
                        <button onClick={() => deleteOperation(op.id)} style={{padding:'2px 8px',background:'#fee2e2',border:'none',borderRadius:4,color:'#ef4444',cursor:'pointer',fontSize:11}}>✕</button>
                      </td>
                    </tr>
                  ))}
                  {current.operations?.length>0 && (
                    <tr style={{background:'#EEEDF8',borderTop:'2px solid #3B3A8F',fontWeight:700}}>
                      <td colSpan={4} style={{padding:'8px 10px',fontSize:13}}>
                        TOTAL SAM
                        <span style={{fontSize:10,fontWeight:400,marginLeft:8,color:hasObservedSAM?'#22c55e':'#f59e0b'}}>
                          {hasObservedSAM ? '(Using Observed Times)' : '(Using Standard SAM)'}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px',fontSize:14,color:'#3B3A8F'}}>{totalSAM.toFixed(2)}</td>
                      <td style={{padding:'8px 10px',fontSize:14,color:'#166534'}}>₹{(current.operations?.reduce((s,op)=>s+(parseFloat(op.cost_per_piece)||0),0)||0).toFixed(2)}</td>
                      <td></td>
                      <td style={{padding:'8px 10px',fontSize:14,color:'#166534',fontWeight:700}}>₹{totalCM.toFixed(2)}</td>
                      <td></td><td></td>
                      <td style={{padding:'8px 10px',fontSize:13}}>{totalMins>0?totalMins.toFixed(0)+' mins':'—'}</td>
                      <td></td><td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Sign off section */}
            <div style={{padding:'16px 20px',borderTop:'1px solid #eee',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>
              {[['Prepared By',current.prepared_by],['Checked By',current.checked_by],['Approved By',current.approved_by]].map(([label,val])=>(
                <div key={label} style={{textAlign:'center'}}>
                  <div style={{borderBottom:'1px solid #333',paddingBottom:20,marginBottom:6,minHeight:30,fontSize:12,color:'#555'}}>{val||''}</div>
                  <div style={{fontSize:11,color:'#888',fontWeight:600}}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Operation Master Panel */}
          <div style={{background:'white',borderRadius:10,boxShadow:'none',border:'1px solid #E8E7E7'}} className="no-print">
            <div style={{padding:'14px 16px',borderBottom:'1px solid #eee'}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#3B3A8F'}}>➕ Add Operation</div>
              <div style={{marginBottom:8}}>
                <label style={labelStyle}>1. Division</label>
                <select value={opDivision} onChange={e=>{setOpDivision(e.target.value);fetchOperationMaster();}} style={inputStyle}>
                  <option value="">All Divisions</option>
                  <option value="MENS">Mens</option>
                  <option value="LADIES">Ladies</option>
                  <option value="KIDS">Kids</option>
                </select>
              </div>
              <div style={{marginBottom:8}}>
                <label style={labelStyle}>2. Search Operation</label>
                <input value={opSearch} onChange={e=>{setOpSearch(e.target.value);fetchOperationMaster();}}
                  placeholder="Type operation name..." style={inputStyle}/>
              </div>
              <div style={{marginBottom:8}}>
                <label style={labelStyle}>3. Frequency (times per garment)</label>
                <input type="number" min="1" defaultValue="1" id="freq-input"
                  style={inputStyle} placeholder="1"/>
                <div style={{fontSize:10,color:'#aaa',marginTop:2}}>e.g. 2 for side pockets (left + right)</div>
              </div>
            </div>
            <div style={{height:420,overflowY:'auto'}}>
              {operations.length===0 ? (
                <div style={{padding:20,textAlign:'center',color:'#aaa',fontSize:12}}>
                  {opDivision||opSearch ? 'No operations found' : 'Select division or search to find operations'}
                </div>
              ) : operations.map((op,i)=>(
                <div key={i}
                  style={{padding:'10px 16px',borderBottom:'1px solid #f5f5f5',cursor:'pointer'}}
                  onMouseEnter={e=>e.currentTarget.style.background='#EEEDF8'}
                  onMouseLeave={e=>e.currentTarget.style.background='white'}
                  onClick={() => {
                    const freq = parseInt(document.getElementById('freq-input')?.value)||1;
                    addOperationWithFreq(op, freq);
                  }}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,color:'#333'}}>{op.process_name}</div>
                      <div style={{fontSize:10,color:'#888',marginTop:2}}>{op.division} · {op.machine_code}</div>
                    </div>
                    <div style={{textAlign:'right',marginLeft:8}}>
                      {op.cost_per_piece && <div style={{fontSize:11,fontWeight:700,color:'#166534'}}>₹{op.cost_per_piece}</div>}
                      {op.standard_sam && <div style={{fontSize:10,color:'#3B3A8F'}}>{op.standard_sam}m SAM</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Operation Modal */}

      {editingRow && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:12,padding:24,width:380}}>
            <div style={{ background: '#2D2C72', margin: '-24px -24px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Update Operation Times</h3></div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div style={{gridColumn:'1/-1'}}>
                <label style={{fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'}}>Operator Name</label>
                <input style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'}}
                  value={rowForm.operator_name||''} onChange={e=>setRowForm(p=>({...p,operator_name:e.target.value}))} placeholder="Operator name"/>
              </div>
              <div>
                <label style={{fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'}}>Start Time</label>
                <input type="time" style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'}}
                  value={rowForm.start_time||''} onChange={e=>setRowForm(p=>({...p,start_time:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'}}>End Time</label>
                <input type="time" style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'}}
                  value={rowForm.end_time||''} onChange={e=>setRowForm(p=>({...p,end_time:e.target.value}))}/>
              </div>
              {rowForm.start_time && rowForm.end_time && (
                <div style={{gridColumn:'1/-1',background:'#EEEDF8',borderRadius:6,padding:'8px 12px',fontSize:12}}>
                  ⏱️ Time consumed: <b>{(()=>{const[sh,sm]=rowForm.start_time.split(':').map(Number);const[eh,em]=rowForm.end_time.split(':').map(Number);return (eh*60+em)-(sh*60+sm);})()  } mins</b>
                </div>
              )}
              <div style={{gridColumn:'1/-1'}}>
                <label style={{fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'}}>Remarks</label>
                <input style={{width:'100%',padding:'8px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'}}
                  value={rowForm.remarks||''} onChange={e=>setRowForm(p=>({...p,remarks:e.target.value}))} placeholder="Any remarks..."/>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'flex-end'}}>
              <button onClick={()=>setEditingRow(null)} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
              <button onClick={()=>updateOperation(editingRow)} disabled={saving} style={{padding:'8px 18px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Saving...':'Save Times'}</button>
            </div>
          </div>
        </div>
      )}
      {showAddOp && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:12,padding:28,width:480}}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Add Custom Operation</h3></div>
            <form onSubmit={addCustomOperation}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Operation Name *</label><input style={inputStyle} required onChange={e=>setOpForm(p=>({...p,process_name:e.target.value}))}/></div>
                <div><label style={labelStyle}>Process Code</label><input style={inputStyle} onChange={e=>setOpForm(p=>({...p,process_code:e.target.value}))}/></div>
                <div><label style={labelStyle}>Machine Type *</label>
                  <select style={inputStyle} required onChange={e=>setOpForm(p=>({...p,machine_code:e.target.value}))}>
                    <option value="">Select</option>
                    {machines.map(m=><option key={m.code} value={m.code}>{m.name}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>SAM (mins)</label><input style={inputStyle} type="number" step="0.01" onChange={e=>setOpForm(p=>({...p,sam:e.target.value}))}/></div>
                <div><label style={labelStyle}>Frequency *</label><input style={inputStyle} type="number" min="1" defaultValue="1" onChange={e=>setOpForm(p=>({...p,frequency:e.target.value}))}/><div style={{fontSize:10,color:'#aaa',marginTop:2}}>How many times this operation per garment</div></div>
                <div><label style={labelStyle}>Operator Name</label><input style={inputStyle} onChange={e=>setOpForm(p=>({...p,operator_name:e.target.value}))}/></div>
                <div><label style={labelStyle}>Start Time</label><input style={inputStyle} type="time" onChange={e=>setOpForm(p=>({...p,start_time:e.target.value}))}/></div>
                <div><label style={labelStyle}>End Time</label><input style={inputStyle} type="time" onChange={e=>setOpForm(p=>({...p,end_time:e.target.value}))}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Remarks</label><input style={inputStyle} onChange={e=>setOpForm(p=>({...p,remarks:e.target.value}))}/></div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowAddOp(false)} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
                <button type="submit" disabled={saving} style={{padding:'8px 18px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Adding...':'Add Operation'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Master Operation Modal */}
      {showNewOpMaster && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:12,padding:28,width:480}}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Add New Operation to Master List</h3></div>
            <form onSubmit={saveNewOpMaster}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={labelStyle}>Division *</label>
                  <select style={inputStyle} required onChange={e=>setNewOpForm(p=>({...p,division:e.target.value}))}>
                    <option value="">Select</option>
                    <option value="MENS">Mens</option><option value="LADIES">Ladies</option><option value="KIDS">Kids</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Process Code *</label><input style={inputStyle} required onChange={e=>setNewOpForm(p=>({...p,process_code:e.target.value.toUpperCase()}))}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Process Name *</label><input style={inputStyle} required onChange={e=>setNewOpForm(p=>({...p,process_name:e.target.value}))}/></div>
                <div><label style={labelStyle}>Machine Type *</label>
                  <select style={inputStyle} required onChange={e=>setNewOpForm(p=>({...p,machine_code:e.target.value}))}>
                    <option value="">Select</option>
                    {machines.map(m=><option key={m.code} value={m.code}>{m.name}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Standard SAM (mins)</label><input style={inputStyle} type="number" step="0.001" onChange={e=>setNewOpForm(p=>({...p,standard_sam:e.target.value}))}/></div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowNewOpMaster(false)} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
                <button type="submit" disabled={saving} style={{padding:'8px 18px',background:'#E22D2D',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Saving...':'Add to Master'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return null;
}

export default function ProductionBulletin() {
  return <BulletinContent />;
}
